import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useClientAccessQuery } from './api/useClientAccessQuery';
import {
  currentClientSelector,
  showFinancialPlanningSelector,
} from '../AdviserArea/AdviserAreaSelector';
import { DashboardFeatureId } from '../AdviserArea/AdviserArea.types';
import { OnboardingWorkflow } from '../CommonSelectors/CommonArea.types';

interface Params {
  clientId: string;
  defaultOnboardingWorkflow?: OnboardingWorkflow;
}

export const useClientAccess = ({ clientId, defaultOnboardingWorkflow }: Params) => {
  const queryInfo = useClientAccessQuery({ clientId, defaultOnboardingWorkflow });
  const showFinancialPlanning = useSelector(showFinancialPlanningSelector);
  const currentClient = useSelector(currentClientSelector);

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    // This filter will be done on BE.
    // FE will do it in the meantime, since it is simpler than BE and is time sensitive.
    // https://dev.azure.com/orionadvisor/Orion%20Agile/_workitems/edit/231337
    //
    const financialPlanOptions = [
      DashboardFeatureId.FinancialPlan,
      DashboardFeatureId.WhatIfs,
      DashboardFeatureId.EditProfile,
    ];

    return {
      ...queryInfo.data,
      dashboardAccess: queryInfo.data.dashboardAccess
        ? queryInfo.data.dashboardAccess.filter(value => {
            if (financialPlanOptions.includes(value)) {
              return showFinancialPlanning;
            }

            if (value === DashboardFeatureId.Newsfeed) {
              return currentClient && currentClient.IsOrionClient;
            }

            return true;
          })
        : null,
    };
  }, [currentClient, queryInfo.data, showFinancialPlanning]);

  return {
    ...queryInfo,
    data,
  };
};
